import { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { appsApi } from "@/api/apps";
import { chatApi, type ChatMessage } from "@/api/chat";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import { ThinkingIndicator } from "@/components/chat/thinking-indicator";
import { AgentChatAvatar } from "@/components/chat/agent-chat-avatar";
import { MarkdownRenderer } from "@/components/chat/markdown-renderer";
import {
  ArrowUp,
  ArrowLeft,
  Bot,
  User,
  Smartphone,
  Tablet,
  Monitor,
  ExternalLink,
  Play,
  Square,
  Rocket,
  RefreshCw,
  Code2,
  Eye,
  Terminal,
} from "lucide-react";

type PreviewDevice = "mobile" | "tablet" | "desktop";

const deviceWidths: Record<PreviewDevice, string> = {
  mobile: "375px",
  tablet: "768px",
  desktop: "100%",
};

export function AppBuilder({
  appId: embeddedAppId,
  onBack,
}: {
  appId?: string;
  onBack?: () => void;
} = {}) {
  const { appId: routeAppId } = useParams<{ appId: string }>();
  const appId = embeddedAppId ?? routeAppId;
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [activeTab, setActiveTab] = useState<"preview" | "code" | "logs">("preview");
  const [device, setDevice] = useState<PreviewDevice>("desktop");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [previewVersion, setPreviewVersion] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Fetch app data
  const { data: app, isLoading } = useQuery({
    queryKey: queryKeys.apps.detail(appId!),
    queryFn: () => appsApi.get(appId!),
    enabled: !!appId,
    refetchInterval: (query) => {
      const d = query.state.data;
      // Poll while building or while dev server is starting up
      return d?.status === "building" || d?.devServerStatus === "starting" ? 3000 : false;
    },
  });

  // Find the App Builder agent
  const { data: allAgents = [] } = useAgentsByCompany(tenantId);
  const appBuilderAgent = allAgents.find((a) => a.role === "app-builder");

  // Get or create a chat session for this app
  useEffect(() => {
    if (!appBuilderAgent || sessionId) return;
    (async () => {
      // Check if this app already has a chat session stored in metadata
      const storedSessionId = (app?.metadata as Record<string, unknown> | null)?.chatSessionId as string | undefined;
      if (storedSessionId) {
        setSessionId(storedSessionId);
        return;
      }

      // Search existing sessions for one linked to this app
      const sessions = await chatApi.listSessions(appBuilderAgent.id);
      const existing = sessions.find(
        (s: { title?: string }) => s.title?.includes(app?.name || appId!)
      );
      if (existing) {
        setSessionId(existing.id);
        // Store it in app metadata for next time
        appsApi.update(appId!, { metadata: { ...(app?.metadata || {}), chatSessionId: existing.id } }).catch(() => {});
        return;
      }

      // Create new session and store in app metadata
      const session = await chatApi.createSession(tenantId, appBuilderAgent.id);
      setSessionId(session.id);
      appsApi.update(appId!, { metadata: { ...(app?.metadata || {}), chatSessionId: session.id } }).catch(() => {});
    })();
  }, [appBuilderAgent, app, appId, tenantId, sessionId]);

  // Messages for current session
  const { data: messages = [] } = useQuery({
    queryKey: ["chat-messages", sessionId],
    queryFn: () => chatApi.getMessages(sessionId!),
    enabled: !!sessionId,
    refetchInterval: 8000,
  });

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingContent]);

  // Dev server controls
  const startDevServer = useMutation({
    mutationFn: () => appsApi.startDevServer(appId!),
    onSuccess: () => {
      // Immediately refetch to pick up "starting" status (triggers polling)
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.detail(appId!) });
      // Bump preview version so iframe loads the "Starting…" page from proxy
      setPreviewVersion((v) => v + 1);
    },
  });

  const stopDevServer = useMutation({
    mutationFn: () => appsApi.stopDevServer(appId!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.apps.detail(appId!) }),
  });

  const promoteMutation = useMutation({
    mutationFn: () => appsApi.promote(appId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.detail(appId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.deployments(appId!) });
    },
  });

  // Send message
  const sendMessage = useCallback(async () => {
    if (!input.trim() || !sessionId || sending) return;

    const userMessage = input.trim();
    setInput("");
    setSending(true);
    setStreamingContent("");

    // Inject app context so the agent knows which app/directory to edit
    const appSlug = app?.name.toLowerCase().replace(/[^a-z0-9-]/g, "-") || "new-app";
    const defaultPath = `~/.openclaw/workspace/${appSlug}`;
    const sourcePath = app?.sourcePath || defaultPath;
    const port = app?.devServerPort || (3000 + Math.floor(Math.random() * 99) + 1);

    let appContext = "";
    if (app?.sourcePath && app.devServerStatus === "running") {
      // Existing app with dev server running — just edit files
      appContext = `[APP CONTEXT: App "${app.name}" at ${sourcePath}, dev server on port ${app.devServerPort}. ` +
        `Edit files in this directory only. The platform-managed dev runtime handles failures and uses nodemon file watching when available.]\n\n`;
    } else {
      // New app — agent creates it and registers source details; the platform starts the managed runtime.
      appContext =
        `[APP CONTEXT: App "${app?.name || "New App"}" (ID: ${app?.id}). ` +
        `Create in ${sourcePath} if it doesn't exist. Use port ${port}.\n` +
        `AFTER creating the app, run this to register it with the platform:\n` +
        `curl -s -X PATCH "$PLATFORM_URL/api/apps/${app?.id}" -H "Authorization: Bearer $AGENT_API_KEY" -H "Content-Type: application/json" ` +
        `-d '{"sourcePath":"${sourcePath.replace("~", "/home/azureuser")}","devServerPort":${port},"devServerStatus":"stopped"}'\n` +
        `Then start the managed preview runtime through the platform:\n` +
        `curl -s -X POST "$PLATFORM_URL/api/apps/${app?.id}/dev-server/start" -H "Authorization: Bearer $AGENT_API_KEY"\n` +
        `Edit files ONLY in ${sourcePath}.]\n\n`;
    }
    const content = appContext + userMessage;

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await fetch(
        `/api/chat/sessions/${sessionId}/messages?stream=true`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ content }),
          signal: controller.signal,
        },
      );

      if (!response.ok || !response.body) {
        setSending(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = "";
      let flushTimer: ReturnType<typeof setTimeout> | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") continue;

          try {
            const parsed = JSON.parse(payload);
            if (parsed.type === "done") continue;
            const delta = parsed.choices?.[0]?.delta?.content;
            if (delta) {
              accumulated += delta;
              // Batch updates every 50ms to reduce React re-renders
              if (!flushTimer) {
                flushTimer = setTimeout(() => {
                  setStreamingContent(accumulated);
                  flushTimer = null;
                }, 50);
              }
            }
          } catch { /* ignore parse errors */ }
        }
      }
      // Flush remaining content
      if (flushTimer) clearTimeout(flushTimer);
      if (accumulated) setStreamingContent(accumulated);
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        console.error("Chat error:", err);
      }
    } finally {
      setSending(false);
      setStreamingContent("");
      abortRef.current = null;
      queryClient.invalidateQueries({ queryKey: ["chat-messages", sessionId] });
      // Refresh app data in case the agent deployed
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.detail(appId!) });
      // Auto-refresh preview after agent responds (give nodemon time to restart)
      // First refresh at 5s, second at 10s for safety
      setTimeout(() => setPreviewVersion((v) => v + 1), 5000);
      setTimeout(() => setPreviewVersion((v) => v + 1), 10000);
    }
  }, [input, sessionId, sending, queryClient, appId]);

  // Preview URL (previewVersion busts cache after agent makes changes)
  const devServerActive = app?.devServerStatus === "running" || app?.devServerStatus === "starting";
  const previewBase = devServerActive
    ? `/api/apps/${appId}/preview/`
    : app?.appUrl || null;
  const previewUrl = previewBase
    ? `${previewBase}${previewBase.includes("?") ? "&" : "?"}_v=${previewVersion}`
    : null;

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-text-muted">Loading...</p>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-text-muted">App not found</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Top bar */}
      <div className="flex shrink-0 items-center justify-between border-b border-border bg-surface px-4 py-2">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              if (onBack) {
                onBack();
                return;
              }
              navigate(`/tenants/${tenantId}/apps`);
            }}
            className="text-text-muted hover:text-accent transition-colors"
          >
            <ArrowLeft size={16} />
          </button>
          <h1 className="text-sm font-semibold text-text-primary">{app.name}</h1>
          <Badge
            variant={
              app.status === "deployed"
                ? "success"
                : app.status === "building"
                  ? "warning"
                  : "secondary"
            }
          >
            {app.status}
          </Badge>
          {app.devServerStatus === "running" && (
            <Badge variant="success">dev server</Badge>
          )}
          {app.devServerStatus === "starting" && (
            <Badge variant="warning">starting…</Badge>
          )}
          {app.devServerStatus === "failed" && (
            <Badge variant="danger">dev failed</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {app.devServerStatus === "running" ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => stopDevServer.mutate()}
              disabled={stopDevServer.isPending}
            >
              <Square size={14} className="mr-1.5" />
              Stop Dev
            </Button>
          ) : app.builtByAgentId ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => startDevServer.mutate()}
              disabled={startDevServer.isPending || app.devServerStatus === "starting"}
            >
              <Play size={14} className="mr-1.5" />
              {app.devServerStatus === "starting" ? "Starting…" : "Start Dev"}
            </Button>
          ) : null}
          {app.appUrl && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(app.appUrl!, "_blank")}
            >
              <ExternalLink size={14} className="mr-1.5" />
              Open
            </Button>
          )}
          <Button
            size="sm"
            disabled={promoteMutation.isPending || !app.builtByAgentId}
            onClick={() => {
              if (confirm("Promote the current dev snapshot to the hosted app? Dev work on the App Builder VM will continue separately.")) {
                promoteMutation.mutate();
              }
            }}
          >
            <Rocket size={14} className="mr-1.5" />
            {promoteMutation.isPending ? "Promoting…" : app.appUrl ? "Promote Update" : "Host App"}
          </Button>
        </div>
      </div>

      {/* Build phase stepper */}
      {app.builtByAgentId && (() => {
        const phases = [
          { key: "requirements", label: "Requirements" },
          { key: "planning", label: "Planning" },
          { key: "building", label: "Building" },
          { key: "testing", label: "Testing" },
          { key: "review", label: "Review" },
          { key: "deploying", label: "Deploying" },
          { key: "deployed", label: "Deployed" },
          { key: "handed_over", label: "Handed Over" },
        ];
        const currentIndex = phases.findIndex((p) => p.key === app.buildPhase);
        return (
          <div className="flex shrink-0 items-center justify-center gap-1 border-b border-border bg-surface px-4 py-2">
            {phases.map((phase, i) => {
              const isCompleted = currentIndex > i;
              const isCurrent = currentIndex === i;
              return (
                <div key={phase.key} className="flex items-center gap-1">
                  {i > 0 && (
                    <div
                      className={cn(
                        "h-px w-4",
                        isCompleted || isCurrent ? "bg-accent" : "bg-border",
                      )}
                    />
                  )}
                  <div className="flex items-center gap-1">
                    <div
                      className={cn(
                        "h-3 w-3 shrink-0 rounded-full",
                        isCompleted
                          ? "bg-accent"
                          : isCurrent
                            ? "bg-accent animate-pulse"
                            : "border border-border bg-transparent",
                      )}
                    />
                    <span
                      className={cn(
                        "text-xs whitespace-nowrap",
                        isCompleted
                          ? "text-accent"
                          : isCurrent
                            ? "font-semibold text-accent"
                            : "text-text-muted",
                      )}
                    >
                      {phase.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Split pane: chat left, preview right.
          On phones the preview is hidden — chat takes the full screen. */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Chat */}
        <div className="flex w-full min-w-0 flex-col border-r border-border sm:w-[400px] sm:min-w-[350px]">
          {/* Chat messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4">
            {!appBuilderAgent ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Bot size={32} className="mb-2 text-text-muted" />
                <p className="text-sm text-text-muted">
                  No App Builder agent found.
                </p>
              </div>
            ) : messages.length === 0 && !streamingContent ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Bot size={32} className="mb-2 text-accent/50" />
                <p className="text-sm text-text-secondary">
                  Describe what you want to build
                </p>
                <p className="mt-1 text-xs text-text-muted">
                  The App Builder agent will create and deploy it for you
                </p>
              </div>
            ) : (
              <>
                {messages.map((msg: ChatMessage) => (
                  <div
                    key={msg.id}
                    className={cn(
                      "mb-4",
                      msg.role === "user" ? "flex justify-end" : "flex justify-start",
                    )}
                  >
                    {msg.role === "assistant" && (
                      <div className="mr-2 mt-0.5 shrink-0">
                        <AgentChatAvatar name={appBuilderAgent.name} avatarUrl={appBuilderAgent.avatarUrl} size="sm" />
                      </div>
                    )}
                    <div
                      className={cn(
                        "max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm",
                        msg.role === "user"
                          ? "bg-accent text-white"
                          : "bg-surface-hover text-text-primary",
                      )}
                    >
                      {msg.role === "assistant" ? (
                        <MarkdownRenderer content={msg.content} />
                      ) : (
                        msg.content.replace(/^\[APP CONTEXT:.*?\]\n\n/s, "")
                      )}
                    </div>
                  </div>
                ))}
                {streamingContent && (
                  <div className="mb-4 flex justify-start">
                    <div className="mr-2 mt-0.5 shrink-0">
                      <AgentChatAvatar name={appBuilderAgent.name} avatarUrl={appBuilderAgent.avatarUrl} size="sm" />
                    </div>
                    <div className="max-w-[85%] rounded-xl bg-surface-hover px-3.5 py-2.5 text-sm text-text-primary">
                      <MarkdownRenderer content={streamingContent} />
                    </div>
                  </div>
                )}
                {sending && !streamingContent && <ThinkingIndicator />}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Chat input */}
          <div className="shrink-0 border-t border-border p-3">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder={
                  appBuilderAgent
                    ? "Describe what to build..."
                    : "Activate App Builder first"
                }
                disabled={!appBuilderAgent || sending}
                className="flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none disabled:opacity-50"
                rows={2}
              />
              <Button
                size="sm"
                onClick={sendMessage}
                disabled={!input.trim() || !appBuilderAgent || sending}
              >
                <ArrowUp size={16} />
              </Button>
            </div>
          </div>
        </div>

        {/* Right: Preview / Code / Logs (hidden on phones — chat takes the whole pane). */}
        <div className="hidden flex-1 flex-col overflow-hidden sm:flex">
          {/* Tab bar */}
          <div className="flex shrink-0 items-center justify-between border-b border-border bg-surface px-4 py-1.5">
            <div className="flex items-center gap-1">
              {[
                { key: "preview" as const, label: "Preview", icon: Eye },
                { key: "code" as const, label: "Code", icon: Code2 },
                { key: "logs" as const, label: "Logs", icon: Terminal },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={cn(
                    "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                    activeTab === key
                      ? "bg-accent/10 text-accent"
                      : "text-text-secondary hover:bg-surface-hover",
                  )}
                >
                  <Icon size={14} />
                  {label}
                </button>
              ))}
            </div>

            {/* Device toggles (preview only) */}
            {activeTab === "preview" && (
              <div className="flex items-center gap-1">
                {[
                  { key: "mobile" as PreviewDevice, icon: Smartphone },
                  { key: "tablet" as PreviewDevice, icon: Tablet },
                  { key: "desktop" as PreviewDevice, icon: Monitor },
                ].map(({ key, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => setDevice(key)}
                    className={cn(
                      "rounded p-1.5 transition-colors",
                      device === key
                        ? "bg-accent/10 text-accent"
                        : "text-text-muted hover:text-text-primary",
                    )}
                  >
                    <Icon size={14} />
                  </button>
                ))}
                <button
                  onClick={() => setPreviewVersion((v) => v + 1)}
                  className="ml-2 rounded p-1.5 text-text-muted hover:text-text-primary transition-colors"
                  title="Refresh preview"
                >
                  <RefreshCw size={14} />
                </button>
              </div>
            )}
          </div>

          {/* Content area */}
          <div className="flex-1 overflow-hidden bg-background">
            {activeTab === "preview" && (
              <div className="flex h-full items-start justify-center overflow-auto bg-[#f0f0f0] p-4">
                {previewUrl ? (
                  <div
                    className="overflow-hidden rounded-lg border border-border bg-white shadow-lg transition-all"
                    style={{
                      width: deviceWidths[device],
                      maxWidth: "100%",
                      height: device === "desktop" ? "100%" : "667px",
                    }}
                  >
                    <iframe
                      key={`${appId}-${previewVersion}`}
                      ref={iframeRef}
                      src={previewUrl}
                      title={app.name}
                      className="h-full w-full"
                      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                    />
                  </div>
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center text-center">
                    <Monitor size={48} className="mb-3 text-text-muted/30" />
                    <p className="text-sm text-text-secondary">No preview available</p>
                    <p className="mt-1 text-xs text-text-muted">
                      {app.devServerStatus === "failed"
                        ? "Dev server failed to start — click Start Dev to retry"
                        : app.builtByAgentId
                          ? "Start the dev server or deploy to see a preview"
                          : "Chat with the App Builder to start building"}
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "code" && (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <Code2 size={48} className="mx-auto mb-3 text-text-muted/30" />
                  <p className="text-sm text-text-secondary">
                    Code viewer coming soon
                  </p>
                  <p className="mt-1 text-xs text-text-muted">
                    Will show source files from the agent&apos;s workspace
                  </p>
                </div>
              </div>
            )}

            {activeTab === "logs" && (
              <div className="h-full overflow-auto bg-[#1e1e1e] p-4 font-mono text-xs text-green-400">
                {app.status === "building" ? (
                  <p>Deploying...</p>
                ) : app.status === "deployed" ? (
                  <p>Last deploy: {app.currentVersion || "unknown"} — {app.appUrl}</p>
                ) : (
                  <p className="text-text-muted">No build logs yet. Start building an app to see logs here.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
